import { Download, Loader2 } from 'lucide-react';
import { formatMoney } from '@/shared/lib/format/money';
import { Pager, StatusPill, Th } from '../WalletPagePrimitives';
import { fmtDisplayDate } from '../walletPage.utils';
import type { Settlement } from '../WalletPage.types';

type SettlementsTableProps = {
  settlements: Settlement[];
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  isFetching?: boolean;
  onPageChange: (page: number) => void;
  invoiceLoadingId?: string;
  onViewInvoice: (settlement: Settlement) => void;
};

export function SettlementsTable({
  settlements,
  page,
  totalPages,
  total,
  pageSize,
  isFetching = false,
  onPageChange,
  invoiceLoadingId,
  onViewInvoice,
}: SettlementsTableProps) {
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px] text-left text-sm">
          <thead>
            <tr className="border-b border-[var(--dash-line)]">
              <Th>Bank</Th>
              <Th>Details</Th>
              <Th>Amount</Th>
              <Th>Status</Th>
              <Th>Date</Th>
              <Th className="text-center">Invoice</Th>
            </tr>
          </thead>
          <tbody>
            {settlements.map((s) => {
              const status = s.transactionId?.paymentStatus || s.status || 'PENDING';
              const loading = invoiceLoadingId === s.wallet?._id;
              return (
                <tr
                  key={s._id}
                  className="border-b border-[var(--dash-line)] last:border-0 hover:bg-[var(--dash-soft)]/40"
                >
                  <td className="px-4 py-3 font-medium text-[var(--dash-ink)]">
                    {s.bankDetailId?.bankName || 'Bank Transfer'}
                  </td>
                  <td className="px-4 py-3 text-xs text-[var(--dash-muted)]">
                    {s.bankDetailId?.accountNumber ? `Acct: ${s.bankDetailId.accountNumber}` : '—'}
                    {s.bankDetailId?.ifscCode ? ` · IFSC: ${s.bankDetailId.ifscCode}` : ''}
                  </td>
                  <td className="px-4 py-3 font-semibold text-[var(--dash-ink)]">
                    {formatMoney(s.amount)}
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill value={status} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-[var(--dash-muted)]">
                    {fmtDisplayDate(s.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      type="button"
                      onClick={() => onViewInvoice(s)}
                      disabled={!s.wallet?._id || loading}
                      title={
                        s.wallet?._id
                          ? 'Download invoice'
                          : 'Invoice not available for this withdrawal'
                      }
                      aria-label={
                        s.wallet?._id
                          ? 'Download invoice'
                          : 'Invoice not available for this withdrawal'
                      }
                      className="rounded-md p-1.5 text-[var(--dash-muted)] hover:bg-[var(--dash-soft)] hover:text-[var(--dash-brand)] disabled:opacity-50"
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pager
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={pageSize}
        isFetching={isFetching}
        onChange={onPageChange}
      />
    </>
  );
}
